/* eslint-disable no-irregular-whitespace */
import React, { useRef, useState } from 'react'
import { validateWorkEmailInput } from '../../formUtils'
import Box from '../primitives/Box'
import Select from '../Select'
import CTAButton from './CTAButton'
import FormCheckboxContainer from './FormCheckboxContainer'

export default function GetStartedForm({
  formId,
  campaign,
  email,
  onSubmit,
  setEmail,
}) {
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [errorMsg, setErrorMsg] = useState('')
  const [numberOfDevs, setNumberOfDevs] = useState('')
  const [sourceCodeHosting, setSourceCodeHosting] = useState('')
  const [issueTrackers, setIssueTrackers] = useState<string[]>([])
  const [howDidYouHearAboutUs, setHowDidYouHearAboutUs] = useState('')

  const [loading, setLoading] = useState(false)
  const [accepted, setAccepted] = useState(false)

  const emailRef = useRef<HTMLInputElement>(null)

  return (
    <form
      className="form"
      onSubmit={onSubmit(
        {
          firstName,
          lastName,
          email,
          numberOfDevs,
          sourceCodeHosting,
          issueTrackers,
          campaign: campaign?.value,
          howDidYouHearAboutUs,
        },
        { email: emailRef.current },
        setErrorMsg,
        setLoading,
        formId,
        !!campaign,
        numberOfDevs === numberOfDevsOptions[0].value,
      )}
    >
      <div className="row">
        <input
          type="text"
          placeholder="First name"
          required
          onChange={e => setFirstName(e.target.value)}
          value={firstName}
        />
        <input
          type="text"
          placeholder="Last name"
          required
          onChange={e => setLastName(e.target.value)}
          value={lastName}
        />
      </div>
      <input
        ref={emailRef}
        className="email"
        type="email"
        placeholder="Work email"
        minLength={5}
        required
        onChange={e => {
          setEmail(e.target.value)
          validateWorkEmailInput(e.target)
        }}
        value={email}
      />
      <Select
        classNamePrefix="demo-select"
        placeholder="How many engineers work in your company?"
        isSearchable={false}
        onChange={option => setNumberOfDevs(option?.value || '')}
        options={numberOfDevsOptions}
      />
      <Select
        classNamePrefix="demo-select"
        placeholder="Where do you host source code?"
        onChange={option => setSourceCodeHosting(option?.value || '')}
        options={sourceCodeHostingOptions.map(option => ({
          label: option,
          value: option,
        }))}
      />
      <Select
        classNamePrefix="demo-select"
        placeholder="What issue tracking software do you use?"
        onChange={option => setIssueTrackers(option ? [option?.value] : [])}
        options={issueTrackerOptions}
      />
      <div>
        <input
          type="text"
          placeholder="How did you hear about us?"
          maxLength={255}
          onChange={e => setHowDidYouHearAboutUs(e.target.value)}
          value={howDidYouHearAboutUs}
        />
      </div>
      {campaign && (
        <FormCheckboxContainer>
          <input type="checkbox" id="campaign-eligible" required />
          <label htmlFor="campaign-eligible">
            I&apos;m {campaign.audience} and therefore eligible for the free
            analysis
          </label>
        </FormCheckboxContainer>
      )}
      <FormCheckboxContainer>
        <input
          type="checkbox"
          id="consent"
          required
          checked={accepted}
          onChange={() => setAccepted(!accepted)}
        />
        <label htmlFor="consent">
          I agree to receive email communication in accordance with
          Swarmia&#8217;s{' '}
          <a href="/privacy/" target="_blank">
            Privacy Policy
          </a>
          .
        </label>
      </FormCheckboxContainer>
      <Box fontFamily="FactorA">
        <CTAButton disabled={loading} className="primary">
          {campaign ? 'Request free analysis' : 'Get started now'}
        </CTAButton>
      </Box>
      <div>{errorMsg}</div>
    </form>
  )
}

export const numberOfDevsOptions = [
  {
    label: '1-9',
    value: '1-9',
  },
  {
    label: '10-30',
    value: '10-30',
  },
  {
    label: '31-50',
    value: '31-50',
  },
  {
    label: '51-100',
    value: '51-100',
  },
  {
    label: '101-250',
    value: '101-250',
  },
  {
    label: '251-500',
    value: '251-500',
  },
  {
    label: '501-1000',
    value: '501-1000',
  },
  {
    label: '1000+',
    value: '1000+',
  },
]

const sourceCodeHostingOptions = [
  'GitHub',
  'GitHub Enterprise Cloud',
  'GitHub Enterprise Server',
  'GitLab',
  'Bitbucket',
  'Azure DevOps',
]

const issueTrackerOptions = [
  {
    label: 'Jira Software Cloud',
    value: 'Jira Software Cloud',
  },
  {
    label: 'Jira Software Data Center',
    value: 'Jira Software Data Center',
  },
  {
    label: 'Linear',
    value: 'Linear',
  },
  {
    label: 'GitHub Issues',
    value: 'GitHub Issues',
  },
  {
    label: 'Shortcut',
    value: 'Clubhouse',
  },
  {
    label: 'ClickUp',
    value: 'ClickUp',
  },
  {
    label: 'Asana',
    value: 'Asana',
  },
  {
    label: 'Other',
    value: 'Other',
  },
]

export type ContactData = {
  firstName: string
  lastName: string
  email: string
  numberOfDevs?: string
  sourceCodeHosting?: string
  issueTrackers?: string[]
  campaign?: string
  howDidYouHearAboutUs?: string
}
